import { useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Expand, ImagePlus } from 'lucide-react'
import type { ProjectPhoto } from '@/data/content'

interface PhotoSliderCardProps {
  photos: ProjectPhoto[]
  title: string
  onExpand: (index: number) => void
  priority?: boolean
}

const SHELL =
  'group overflow-hidden rounded-2xl border border-bone-300 bg-bone-50 shadow-[0_16px_40px_-34px_rgba(11,26,18,0.5)] transition hover:border-forest-950/20 hover:shadow-[0_22px_50px_-30px_rgba(11,26,18,0.55)]'

export default function PhotoSliderCard({
  photos,
  title,
  onExpand,
  priority = false,
}: PhotoSliderCardProps) {
  const [index, setIndex] = useState(0)
  const touchStart = useRef<number | null>(null)
  const total = photos.length

  if (total === 0) {
    return (
      <div
        aria-hidden="true"
        className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-forest-950/15 bg-bone-50/60 text-center"
      >
        <ImagePlus className="h-5 w-5 text-forest-950/25" />
        <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-forest-950/35">
          Photo slot
        </span>
      </div>
    )
  }

  const go = (step: number) => setIndex(prev => (prev + step + total) % total)
  const active = photos[Math.min(index, total - 1)]

  return (
    <figure className={SHELL}>
      <div
        className="relative aspect-[4/3] w-full touch-pan-y overflow-hidden bg-forest-950/5"
        onTouchStart={event => {
          touchStart.current = event.touches[0].clientX
        }}
        onTouchEnd={event => {
          if (touchStart.current === null) return
          const delta = event.changedTouches[0].clientX - touchStart.current
          if (Math.abs(delta) > 40) go(delta < 0 ? 1 : -1)
          touchStart.current = null
        }}
      >
        {photos.map((photo, i) => (
          <img
            key={photo.src + i}
            src={photo.src}
            alt={photo.alt}
            loading={priority && i === 0 ? 'eager' : 'lazy'}
            aria-hidden={i !== index}
            className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-500 group-hover:scale-[1.03] ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}

        <button
          type="button"
          onClick={() => onExpand(index)}
          aria-label={`Enlarge photo ${index + 1} of ${total}: ${active?.alt ?? title}`}
          className="absolute inset-0 z-10 cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-bone-50"
        >
          <span className="sr-only">Open larger view</span>
        </button>

        <span className="pointer-events-none absolute right-3 top-3 z-20 rounded-full bg-forest-950/50 px-2.5 py-1 text-[10px] font-semibold tabular-nums text-bone-50 backdrop-blur-sm">
          {index + 1}/{total}
        </span>
        <span className="pointer-events-none absolute right-3 top-11 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-bone-50/30 bg-forest-950/45 text-bone-50 opacity-0 backdrop-blur-md transition group-hover:opacity-100">
          <Expand className="h-3.5 w-3.5" />
        </span>

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label={`Previous photo: ${title}`}
              className="absolute left-2 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-bone-50/30 bg-forest-950/45 text-bone-50 opacity-0 backdrop-blur-md transition hover:bg-forest-950/75 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bone-50 group-hover:opacity-100"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label={`Next photo: ${title}`}
              className="absolute right-2 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-bone-50/30 bg-forest-950/45 text-bone-50 opacity-0 backdrop-blur-md transition hover:bg-forest-950/75 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bone-50 group-hover:opacity-100"
            >
              <ChevronRight className="h-4 w-4" />
            </button>

            <div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1.5">
              {photos.map((photo, i) => (
                <button
                  key={`dot-${photo.src}-${i}`}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Show photo ${i + 1} of ${total}`}
                  aria-current={i === index}
                  className={`h-1.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bone-50 ${
                    i === index ? 'w-6 bg-bone-50' : 'w-1.5 bg-bone-50/55 hover:bg-bone-50/80'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </figure>
  )
}
